import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-5378">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5379">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-5380">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-5381">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-5382" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-5383">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-5384" />
              <p className="text-sm text-gray-600" data-id="element-5385">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-5386">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-5387" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-5388">
          {/* Quality & Metadata */}
          <div data-id="element-5389">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5390">
              <Brain className="w-3.5 h-3.5" data-id="element-5391" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-5392">
              <div className="grid grid-cols-2 gap-4" data-id="element-5393">
                <div data-id="element-5394">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5395">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-5396">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-5397">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5398">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-5399">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-5400">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-5401">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div data-id="element-5402">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5403">Vraagtype</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5404">
                    {row.questionType}
                  </p>
                </div>
                <div data-id="element-5405">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5406">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5407">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-5408">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5409">
              <Database className="w-3.5 h-3.5" data-id="element-5410" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-5411">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5412" />
                <span className="text-sm text-gray-600" data-id="element-5413">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-5414">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-5415">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-5416">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5417">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-5418" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-5419">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-5420">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5421" />
                  <span className="text-sm" data-id="element-5422">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-5423">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5424" />
                  <span className="text-sm" data-id="element-5425">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-5426">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5427">
              <BookOpen className="w-3.5 h-3.5" data-id="element-5428" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-5429">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5430">
                      <div data-id="element-5431">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5432">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5433">
                          {row.pipelineIssues.some(i => i.code === 'SEED_DOK_MISSING') ? <span className="text-amber-600" data-id="element-5434">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-5435">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5436">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5437">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-5438">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_PARTIAL') ? <span className="text-amber-500" data-id="element-5439">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-5440">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-5441">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5442" />
                        <span className="text-sm text-gray-600" data-id="element-5443">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-5444">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-5445" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-5446" />}
                        <span className="text-sm text-gray-600" data-id="element-5447">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}